import MessageFormat from "@messageformat/core";
import { Lang } from "../types";
import enAppMessages from "./app-messages.en.yaml";
import fiAppMessages from "./app-messages.fi.yaml";
import enConsentMessages from "./consent-messages.en.yaml";
import fiConsentMessages from "./consent-messages.fi.yaml";
import enCountries from "./countries.en.yaml";
import fiCountries from "./countries.fi.yaml";
import enSiteMessages from "./site-messages.en.yaml";
import fiSiteMessages from "./site-messages.fi.yaml";
import enCoreMessages from "./core-messages.en.yaml";
import fiCoreMessages from "./core-messages.fi.yaml";

type MessageFunction = (param?: Record<string, unknown> | unknown[]) => string;

type MessageValue = boolean | number | string | MessageFunction;

type Messages = {
  [key: string]: MessageValue | Messages;
};

/**
 * Recursively compile ICU MessageFormat strings into functions.
 * This is required for @messageformat/react to handle variable interpolation,
 * pluralization, and other ICU MessageFormat features.
 */
function compileMessages(
  messages: Record<string, unknown>,
  mf: MessageFormat,
): Messages {
  const compiled: Messages = {};

  for (const [key, value] of Object.entries(messages)) {
    if (typeof value === "string") {
      // Compile ICU MessageFormat string into a function
      try {
        compiled[key] = mf.compile(value);
      } catch (error) {
        console.error(`Failed to compile message "${key}": ${value}`, error);
        // Fallback to the original string if compilation fails
        compiled[key] = value;
      }
    } else if (typeof value === "object" && value !== null) {
      // Recursively compile nested objects
      compiled[key] = compileMessages(value as Record<string, unknown>, mf);
    } else {
      // Keep non-string values as-is (numbers, booleans)
      compiled[key] = value as MessageValue;
    }
  }

  return compiled;
}

// Create MessageFormat compilers for each locale
const mfEn = new MessageFormat("en");
const mfFi = new MessageFormat("fi");

// Type-safe message exports compatible with MessageProvider
// Country translations are merged at the top level as "country"
// All ICU MessageFormat strings are compiled into functions
export const appMessages: { [key in Lang]: Messages } = {
  en: compileMessages(
    {
      ...(enAppMessages as Record<string, unknown>),
      ...(enCoreMessages as Record<string, unknown>),
      country: enCountries as Record<string, unknown>,
    },
    mfEn,
  ),
  fi: compileMessages(
    {
      ...(fiAppMessages as Record<string, unknown>),
      ...(fiCoreMessages as Record<string, unknown>),
      country: fiCountries as Record<string, unknown>,
    },
    mfFi,
  ),
};

// Site messages (separate from main app messages)
export const siteMessages: { [key in Lang]: Messages } = {
  en: compileMessages(
    {
      ...(enSiteMessages as Record<string, unknown>),
      ...(enCoreMessages as Record<string, unknown>),
      country: enCountries as Record<string, unknown>,
    },
    mfEn,
  ),
  fi: compileMessages(
    {
      ...(fiSiteMessages as Record<string, unknown>),
      ...(fiCoreMessages as Record<string, unknown>),
      country: fiCountries as Record<string, unknown>,
    },
    mfFi,
  ),
};

// Consent messages for components rendered outside the main MessageProvider
// (e.g., MarketingConsentBanner in gatsby-browser.tsx)
export const consentMessages: { [key in Lang]: Messages } = {
  en: compileMessages(enConsentMessages as Record<string, unknown>, mfEn),
  fi: compileMessages(fiConsentMessages as Record<string, unknown>, mfFi),
};
